import { useQueryClient } from '@tanstack/react-query';
import React, { FC, FormEvent } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Label } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import Note from '@/components/Forms/Note';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import usePublicHolidayListsOptions from '@/hooks/usePublicHolidayListsOptions';
import { FreelancerVacationBalanceResource } from '@/types/resources';
import { api } from '@/utils/api';
import { VacationType } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';

type FormDataT = {
  weekend_days: number[];
  reset_date: string;
  vacation_type: VacationType;
  annual_current_days: number;
  allocated_days: number;
  can_request_since: string;
  public_holiday_list_id: number | null;
};

const WeekendsField = styled.div`
  grid-column: span 2;
`;

const DaysHolder = styled.div`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: repeat(3, 1fr);
`;

const DaySelection = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const DayName = styled.div`
  width: 100%;
  height: 39px;
  border: solid 1px ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  align-items: center;
  justify-content: center;
  display: flex;
  transition: all ease-out 0.2s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.darkGray};
  }
`;

const Input = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 1px;
  width: 1px;
  &:checked + ${DayName} {
    border-color: ${({ theme }) => theme.colors.purple};
    background-color: ${({ theme }) => theme.colors.purple};
  }
`;

const StyledNote = styled(Note)`
  grid-column: span 2;
`;

const days = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

const vacationTypeOptions = [
  { value: VacationType.Instant, label: 'Instant' },
  { value: VacationType.Annual, label: 'Annual' },
];

interface Props {
  balance: FreelancerVacationBalanceResource;
  onClose: () => void;
}

const FreelancerEditVacationModal: FC<Props> = ({ balance, onClose }) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const alerts = useAlerts();
  const { data: publicHolidayListsOptions = [] } =
    usePublicHolidayListsOptions();

  const methods = useForm<FormDataT>({
    defaultValues: {
      weekend_days: balance.weekendDays,
      reset_date: balance.resetDate ?? '',
      vacation_type: balance.vacationType ?? VacationType.Instant,
      annual_current_days: balance.annualCurrentDays ?? 0,
      allocated_days: balance.allocatedDays ?? 0,
      can_request_since: balance.canRequestSince ?? '',
      public_holiday_list_id: balance.publicHolidayListId ?? null,
    },
  });

  const vacationType = methods.watch('vacation_type');

  const {
    control,
    handleSubmit,
    clearErrors,
    setError,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const payload = {
          ...data,
          annual_current_days: Number(data.annual_current_days),
          allocated_days: Number(data.allocated_days),
        };
        await api.put<never, { id: number }>(
          `/v1/payments/freelancers/holidays/balance/${id}`,
          payload,
        );

        await queryClient.invalidateQueries(['freelancer_vacation_requests'], {
          exact: false,
        });

        await queryClient.invalidateQueries(['freelancer_vacation_balance'], {
          exact: false,
        });

        alerts.success(`Vacation settings updated successfully`);

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, false, true);
      }
    })(e);
  };

  return (
    <SidePanel heading={`Edit Vacation Settings`} onClose={onClose}>
      <SidePanelBody onSubmit={onSubmit} autoComplete="off">
        <SidePanelContent>
          <FormLayout>
            <WeekendsField>
              <Label>Weekend Days</Label>
              <Controller
                name="weekend_days"
                control={control}
                render={({ field }) => (
                  <DaysHolder>
                    {days.map((day) => {
                      const isChecked = field.value.includes(day.value);
                      return (
                        <DaySelection key={day.value}>
                          <Input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const newValue = e.target.checked
                                ? [...field.value, day.value]
                                : field.value.filter((v) => v !== day.value);
                              field.onChange(newValue);
                            }}
                          />
                          <DayName>
                            <Text
                              $type="smallHeadline"
                              $color={isChecked ? 'white' : 'black'}
                            >
                              {day.label}
                            </Text>
                          </DayName>
                        </DaySelection>
                      );
                    })}
                  </DaysHolder>
                )}
              />
            </WeekendsField>
            <FormField<FormDataT, number | null>
              control={control}
              name="public_holiday_list_id"
              type="select"
              options={publicHolidayListsOptions}
              label="Public Holidays"
            />
            <FormField<FormDataT>
              control={control}
              name="vacation_type"
              type="select"
              options={vacationTypeOptions}
              label="Vacation Type"
            />
            {vacationType === VacationType.Instant && (
              <>
                <StyledNote>
                  Reset period — when vacation days refresh. By default, it's
                  set every year.
                </StyledNote>
                <FormField<FormDataT>
                  control={control}
                  name="reset_date"
                  type="datepicker"
                  datepickerType="date"
                  label="Vacation Reset Date"
                  disableDayDimming
                />
                <FormField<FormDataT>
                  control={control}
                  name="can_request_since"
                  type="datepicker"
                  datepickerType="date"
                  label="Can Request Since"
                  disableDayDimming
                />
                <FormField<FormDataT>
                  control={control}
                  name="allocated_days"
                  placeholder="Add Days"
                  type="integer"
                  label="Allocated Days"
                />
              </>
            )}
            {vacationType === VacationType.Annual && (
              <FormField<FormDataT>
                control={control}
                name="annual_current_days"
                placeholder="Add Days"
                type="decimal"
                label="Allocated Days"
              />
            )}
          </FormLayout>
        </SidePanelContent>
        <SidePanelFooter $actions>
          <Button disabled={isSubmitting} loading={isSubmitting} type="submit">
            Submit
          </Button>
          <Button color="secondary" onClick={onClose} type="button">
            Close
          </Button>
        </SidePanelFooter>
      </SidePanelBody>
    </SidePanel>
  );
};

export default FreelancerEditVacationModal;
